/**
 * 文件上传管理 Composable
 * 提供文件上传的完整功能，包括状态管理、进度跟踪、批量上传等
 */

import { ref, computed, reactive } from 'vue'
import messageUtil from '@/utils/message'

/**
 * 文件上传管理 Hook
 * @param {Object} options - 配置选项
 * @param {number} [options.maxSize=500] - 单个文件最大大小(MB)
 * @param {number} [options.maxLimit=6] - 最大文件数量限制
 * @param {boolean} [options.multiple=true] - 是否支持多文件上传
 * @param {string[]} [options.accept] - 允许的文件类型
 * @param {boolean} [options.autoUpload=false] - 是否自动上传
 * @param {boolean} [options.drag=false] - 是否支持拖拽上传
 * @returns {Object} 上传相关的方法和状态
 */
export function useFileUpload(options = {}) {
  // 默认配置
  const uploadConfig = {
    maxSize: 500, // MB
    maxLimit: 6,
    multiple: true,
    accept: ['.zip', '.doc', '.docx', '.ppt', '.pptx', '.xlsx', '.csv', '.pdf'],
    autoUpload: false,
    ...options,
  }

  // 响应式状态
  const loading = ref(false)
  const uploadState = ref(null)
  const fileList = ref([])
  const fileUploadRefs = ref({})
  const replaceFile = ref(null) // 替换文件

  // 计算属性
  const hasFilesToUpload = computed(() => {
    return fileList.value.some(item => item.files && item.files.length > 0)
  })

  const uploadableFilesCount = computed(() => {
    return fileList.value.reduce((total, item) => {
      if (!item.files) return total
      return (
        total +
        item.files.filter(file => file.status && !['success', 'uploading'].includes(file.status))
          .length
      )
    }, 0)
  })

  /**
   * 检查文件数量是否超过限制
   * @param {Array} files - 文件列表
   * @returns {boolean} 是否超过限制
   */
  const checkMaxLimit = files => {
    const { maxLimit } = uploadConfig
    if (maxLimit && files.length >= maxLimit) {
      messageUtil.warning(`当前限制选择 ${maxLimit} 个文件`)
      return true
    }
    return false
  }

  /**
   * 创建文件对象
   * @param {Object} file - 文件对象
   * @param {Object} [originFile] - 原始文件对象
   * @returns {Object} 新的文件对象
   */
  const createFileObject = (file, originFile) => {
    return {
      ...file,
      origin: originFile,
      isReplaced: !!originFile,
    }
  }

  /**
   * 处理文件进度更新
   * @param {string} fileId - 文件ID
   * @param {Object} state - 状态对象
   */
  const handleFileProgress = (fileId, state) => {
    fileList.value.forEach(item => {
      if (item.files) {
        const fileIndex = item.files.findIndex(
          file => file.fileId === fileId || file.uid === fileId,
        )
        if (fileIndex !== -1) {
          const updatedFile = {
            ...item.files[fileIndex],
            ...state,
          }
          item.files.splice(fileIndex, 1, updatedFile)
        }
      }
    })
  }

  /**
   * 处理上传状态变化
   * @param {Object} state - 上传状态
   */
  const handleUploadState = state => {
    uploadState.value = state
    loading.value = state.isUploading
  }

  /**
   * 处理文件上传完成
   * @param {Object} result - 上传结果
   * @param {string} fileId - 文件ID
   */
  const handleUploadComplete = (result, fileId) => {
    fileList.value.forEach(item => {
      if (item.files) {
        const fileIndex = item.files.findIndex(f => f.fileId === fileId || f.uid === fileId)
        if (fileIndex !== -1) {
          const updatedFile = {
            ...item.files[fileIndex],
            status: 'success',
            progress: 100,
            result,
          }
          item.files.splice(fileIndex, 1, updatedFile)
        }
      }
    })
  }

  /**
   * 处理文件选择
   * @param {Array} files - 文件列表
   * @param {Object} currentFile - 当前文件
   * @param {Object} context - 目标项目
   */
  const handleFilesSelected = (files, currentFile, context) => {
    let targetItem = null
    if (!context?.keywordId) {
      targetItem = fileList.value?.[0]
    } else {
      targetItem = fileList.value.find(listItem => listItem.keywordId === context.keywordId)
    }

    if (targetItem) {
      if (replaceFile.value) {
        const reIndex = targetItem.files.findIndex(file => file.uid === replaceFile.value.uid)
        if (reIndex !== -1 && files.length > 0) {
          const newFile = createFileObject(currentFile, replaceFile.value)
          targetItem.files.splice(reIndex, 1, newFile)
        }
        replaceFile.value = null
      } else {
        if (checkMaxLimit(targetItem.files)) {
          return
        }
        targetItem.files.push(currentFile)
      }
    }
  }

  /**
   * 处理文件替换
   * @param {Object} item - 目标项目
   * @param {number} fileIndex - 文件索引
   * @param {string} refKey - 组件引用键名
   */
  const handleReplace = (item, fileIndex, refKey) => {
    if (item.files && item.files[fileIndex]) {
      replaceFile.value = { ...item.files[fileIndex] }
      const uploadRef = fileUploadRefs.value[refKey]
      if (uploadRef?.handleSelect) {
        uploadRef.handleSelect()
      }
    }
  }

  /**
   * 手动上传清空替换文件
   */
  const manuallyUpload = () => {
    replaceFile.value = null
  }

  /**
   * 处理文件删除
   * @param {Object} item - 目标项目
   * @param {number} fileIndex - 文件索引
   */
  const handleDelete = (item, fileIndex) => {
    item.files.splice(fileIndex, 1)
  }

  /**
   * 断点续传
   * @param {Object} file - 文件对象
   */
  const handleRetry = file => {
    const uploadRef = fileUploadRefs.value[`fileUploadRef-${file.itemIndex}`]
    if (uploadRef?.resumeUpload) {
      uploadRef.resumeUpload(file.taskId)
    }
  }

  /**
   * 设置文件上传组件引用
   * @param {string} key - 引用键名
   * @param {Object} uploadRef - 组件引用
   */
  const setFileUploadRef = (key, uploadRef) => {
    if (uploadRef) {
      fileUploadRefs.value[key] = uploadRef
    } else {
      delete fileUploadRefs.value[key]
    }
  }

  /**
   * 批量上传所有文件
   * @param {Function} [onComplete] - 上传完成回调
   * @returns {Promise<Object>} 上传结果
   */
  const batchUploadFiles = async (onComplete = null) => {
    if (!hasFilesToUpload.value || uploadableFilesCount.value === 0) {
      if (onComplete && typeof onComplete === 'function') {
        await onComplete(fileList.value)
      }
      return { success: true, message: '没有需要上传的文件' }
    }

    loading.value = true
    try {
      const uploadPromises = fileList.value
        .map((item, index) => {
          if (!item.files?.length) return null

          const filesToUpload = item.files.filter(
            file => file.status !== 'success' && file.status !== 'uploading',
          )

          if (!filesToUpload.length) return null

          const uploadRef = fileUploadRefs.value[`fileUploadRef-${index}`]
          return uploadRef?.batchUploadFiles?.() || null
        })
        .filter(Boolean)

      if (!uploadPromises.length) {
        return { success: false, message: '没有找到可上传的文件' }
      }

      const results = await Promise.all(uploadPromises)
      const failedResults = results.filter(result => !result.success)

      if (failedResults.length > 0) {
        const totalFailed = failedResults.reduce((sum, result) => {
          return sum + (result.failed?.length || 1)
        }, 0)

        messageUtil.error(`${totalFailed} 个文件上传失败`)
        return {
          success: false,
          failed: failedResults,
          message: `${totalFailed} 个文件上传失败`,
        }
      }

      if (onComplete && typeof onComplete === 'function') {
        await onComplete(fileList.value)
      }

      // messageUtil.success('所有文件上传成功')
      return { success: true, results, message: '所有文件上传成功' }
    } catch (error) {
      console.error('上传失败:', error)
      // messageUtil.error('上传过程中发生异常')
      return { success: false, error, message: '上传过程中发生异常' }
    } finally {
      loading.value = false
    }
  }

  return {
    // 响应式状态
    loading,
    uploadState,
    fileList,
    fileUploadRefs,

    // 计算属性
    hasFilesToUpload,
    uploadableFilesCount,
    uploadConfig,

    // 文件操作方法
    handleFileProgress,
    handleUploadState,
    handleUploadComplete,
    handleFilesSelected,
    handleReplace,
    handleDelete,
    handleRetry,
    manuallyUpload,

    // 管理方法
    setFileUploadRef,
    batchUploadFiles,
  }
}

export default useFileUpload
